import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Button } from "../../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { AlertTriangle } from "lucide-react";

/**
 * Confirmação antes de registar um voo numa aeronave com pendências.
 *
 * NUNCA bloqueia o registo: o voo aconteceu e tem de ficar na caderneta. Só mostra o que está
 * errado (aeronave não operacional) ou a pedir atenção (operacional, com avisos) e pergunta
 * "Registar mesmo assim?". Se a verificação falhar por qualquer motivo, deixa registar.
 *
 *   const { checkGate, gateElement } = useAircraftGate();
 *   if (!isEdit && !(await checkGate(registration, flightDate))) return;   // utilizador voltou atrás
 *   ...                                                                      // {gateElement} no JSX
 */
export function useAircraftGate() {
  const [gate, setGate] = useState(null);
  const resolver = useRef(null);

  const checkGate = useCallback(async (registration, flightDate) => {
    const reg = (registration || "").trim();
    if (!reg) return true;
    // Voos antigos são acertos de caderneta: o estado de hoje da aeronave não lhes diz respeito
    if (flightDate) {
      const days = (Date.now() - new Date(`${flightDate}T00:00:00`).getTime()) / 86400000;
      if (days > 7) return true;
    }
    let data;
    try {
      const r = await axios.get(`${API}/di/aircraft/by-registration/${encodeURIComponent(reg)}/gate`, { withCredentials: true });
      data = r.data;
    } catch {
      return true;
    }
    const bloqueios = data?.bloqueios || [];
    const avisos = data?.avisos || [];
    if (!data?.found || (bloqueios.length === 0 && avisos.length === 0)) return true;
    return new Promise((resolve) => {
      resolver.current = resolve;
      setGate({ registration: data.registration || reg, operacional: !!data.operacional, bloqueios, avisos });
    });
  }, []);

  const answer = (ok) => {
    setGate(null);
    if (resolver.current) resolver.current(ok);
    resolver.current = null;
  };

  const gateElement = gate ? (
    <Dialog open onOpenChange={(o) => { if (!o) answer(false); }}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-md max-h-[85vh] overflow-y-auto" data-testid="di-gate-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${gate.operacional ? "text-amber-300" : "text-red-400"}`} />
            {gate.registration}: {gate.operacional ? "operacional, com avisos" : "aeronave não operacional"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            O registo nunca é bloqueado (o voo tem de ficar na caderneta), mas confirme que está ciente do estado da aeronave.
          </DialogDescription>
        </DialogHeader>
        {gate.bloqueios.length > 0 && (
          <ul className="text-xs text-red-300 list-disc pl-5 space-y-1" data-testid="di-gate-bloqueios">
            {gate.bloqueios.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        {gate.avisos.length > 0 && (
          <ul className="text-xs text-amber-300 list-disc pl-5 space-y-1" data-testid="di-gate-avisos">
            {gate.avisos.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        )}
        <DialogFooter className="mt-2 gap-2">
          <Button variant="outline" onClick={() => answer(false)} className="border-slate-600 text-slate-300" data-testid="di-gate-cancel">Voltar</Button>
          <Button onClick={() => answer(true)} className={gate.operacional ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-red-600 hover:bg-red-500 text-white"} data-testid="di-gate-confirm">
            Registar mesmo assim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;

  return { checkGate, gateElement };
}
